import React from 'react';
import PropTypes from 'prop-types';
const _ = {
    find: require('lodash/find'),
    filter: require('lodash/filter'),
    keys: require('lodash/keys'),
    forEach: require('lodash/forEach'),
    clone: require('lodash/clone'),
    isEqual: require('lodash/isEqual'),
    orderBy: require('lodash/orderBy'),
};

const Globals = require('../../../../../app/globals');
const Strings = require('../../../../../app/shared/Strings');
const StringUtil = require('../../../../../app/shared/StringUtil');
import AccessibleTooltip from '../../common/AccessibleTooltip';
import ExpansionButton from '../../common/ExpansionButton';
import LoadingSpinner from '../../loading/LoadingSpinner';

const propTypes = {
    activeMinVersion: PropTypes.string,
    activeMaxVersion: PropTypes.string,
	questionSummary: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			display_name: PropTypes.string.isRequired,
			multiple_attempts: PropTypes.bool.isRequired,
			quality: PropTypes.oneOf(Object.values(Globals.analytics.question_quality)).isRequired,
			question_type: PropTypes.oneOf(Object.values(Globals.analytics.question_type)).isRequired,
			time_spent_total: PropTypes.number.isRequired,
			total_answers: PropTypes.number.isRequired,
			total_correct: PropTypes.number.isRequired,
		})
	),
	requestDetails: PropTypes.func.isRequired,
	questionDetails: PropTypes.object,
    showCharacterWhenZero: PropTypes.bool,
    hideDataColumns: PropTypes.bool,
};
const defaultProps = {
    activeMinVersion: '',
    activeMaxVersion: '',
	questionSummary: [],
    questionDetails: [],
    showCharacterWhenZero: false,
    hideDataColumns: false,
};

const ZERO_CHAR = '-';

// helper components for table headings
function TH_QLA({th_id,tt_id,className,headingText,tooltipText}) {
    return (
        <th id={th_id} className={className}>
            {headingText}
            <AccessibleTooltip
                id={tt_id}
                className="margin-left-sm"
                placement="top"
                icon="fa-question-circle-o"
                aria-label={Strings['en'].common.HELP + ": " + headingText}
            >
                {tooltipText}
            </AccessibleTooltip>
        </th>
    )
}

function TH_QLA_Question({question,partCode,className,headingText,tooltipText}) {
    let th_id =`qlaQdTable-${partCode}-th-${question.id}`;
    let tt_id =`qlaQdTable-${partCode}-tt-${question.id}`;
    return (
        <TH_QLA th_id={th_id} tt_id={tt_id} className={className} headingText={headingText} tooltipText={tooltipText}/>
    );
}

function TH_QLA_Impact({question, impact}) {
    let th_id = `qlaQdTable-qLo-th-${question.id}-${impact.id}`;
    let tt_id = `qlaQdTable-qLo-tt-${question.id}-${impact.id}`;
    return (
        <TH_QLA th_id={th_id} tt_id={tt_id} className="alignLeft wrapText" 
            headingText={impact.name} 
            tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_LO_TT}
        />
    );
}



export default class QLAQuestionSummaryTable extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			expandedQuestions: {},
            pendingDetails: {},
		};
        this.canToggleDetails = this.canToggleDetails.bind(this);
        this.detailsAreExpanded = this.detailsAreExpanded.bind(this);
        this.detailsArePending = this.detailsArePending.bind(this);
        this.detailsAreLoaded = this.detailsAreLoaded.bind(this);
        this.onToggleDetails = this.onToggleDetails.bind(this);
        this.renderMCQDetails = this.renderMCQDetails.bind(this);
        this.renderFIBQDetails = this.renderFIBQDetails.bind(this);
        this.renderATAQDetails = this.renderATAQDetails.bind(this);
        this.renderCATQDetails = this.renderCATQDetails.bind(this);
        this.renderDetails = this.renderDetails.bind(this);
	}
    
    // UNSAFE_componentWillReceiveProps(nextProps) {
    componentDidUpdate(prevProps, prevState) {
        let expandedQuestions;
        let pendingDetails;
        if (
            prevProps.activeMinVersion === this.props.activeMinVersion
            && prevProps.activeMaxVersion === this.props.activeMaxVersion
        ) {
            let hasChange = false;
            const newPendingDetails = { ...this.state.pendingDetails };
            Object.keys(this.state.pendingDetails).forEach((questionId) => {
                if (
                    this.state.pendingDetails[questionId]
                    && !!this.props.questionDetails
                    && !!this.props.questionDetails[questionId]
                ) {
                    hasChange = true;
                    // keep this obj small, preferring prop deletion over setting prop = false
                    // will keep this loop more efficient over time
                    delete newPendingDetails[questionId];
                }
            });
            
            if (hasChange) {
                this.setState({ pendingDetails: newPendingDetails });
            }
        }
        else {
            expandedQuestions = {};
            pendingDetails = {};
            this.setState({ expandedQuestions, pendingDetails });
        }
    }
    
    canToggleDetails=(question)=>{
        if ( // TODO: These question types not yet implemented in details endpoint
            question.question_type === Globals.analytics.question_type.LIKERT
            || question.question_type === Globals.analytics.question_type.RANKED
        ) {
            return false;
        }

        return true;
    }

    getTitleForToggleControl = (question) => {
        if ( // TODO: These question types not yet implemented in details endpoint
            question.question_type === Globals.analytics.question_type.LIKERT
            || question.question_type === Globals.analytics.question_type.RANKED
        ) {
            return Strings['en'].activity.qla.questions.QA_THEAD_NO_DET_FOR_TYPE;
        }

        return '';
    }

    detailsAreExpanded = (question) => {
        if (!!this.state.expandedQuestions[question.id]) {
            return true;
        }

        return false;
    }

    detailsArePending = (question) => {
        return !!this.state.pendingDetails[question.id];
    }

    detailsAreLoaded = (question) => {
        if (this.props.questionDetails && this.props.questionDetails[question.id]) {
            return true;
        }
        return false;
    }
    
    onToggleDetails = (question) => {
        if (this.detailsArePending(question)) return;

        const willExpand = this.detailsAreExpanded(question) ? false : true;
        if (willExpand && !this.detailsAreLoaded(question)) {
            this.props.requestDetails(
                question.id,
                question.question_type,
                () => { // success callback
                    this.setState({
                        expandedQuestions: { ...this.state.expandedQuestions, [question.id]: willExpand },
                        pendingDetails: { ...this.state.pendingDetails, [question.id]: true },
                    });
                },
                () => { // failure callback
                    console.log("detail request already in progress."); // only one at a time. do not expanding or set pending
                }
            );

            return;
        }
        else {
            this.setState({ expandedQuestions: { ...this.state.expandedQuestions, [question.id]: willExpand } });
        }
    }
    
    renderMCQDetails=(details,question)=>{
        let categoryNames = [];
        let headingMap = {};
        let grandTotalCount = 0; // for percent calculation
        var isOpinion = (question.quality === Globals.analytics.question_quality.OPINION);
        
        _.forEach(details,(row)=>{
            grandTotalCount += (Number(row.count) || 0);
            _.forEach(row.impacts,(impact)=>{
                if (typeof headingMap[impact.id] === 'undefined') {
                    headingMap[impact.id] = categoryNames.length;
                    categoryNames.push(
                        <TH_QLA_Impact key={impact.id} question={question} impact={impact}/>
                    );
                }
            });
        });
        let rowElems = [];
        _.forEach(details,(row)=>{
            let categoryImpacts = [];
            _.forEach(headingMap,(index,impact_id)=>{
                const imp = _.find(row.impacts,{id: impact_id});
                categoryImpacts[index] = (
                    <td className="alignCenter" key={index}>
                        { (imp && imp.impact) ? imp.impact : '' }
                    </td>
                );
            });
            rowElems.push(
                <tr key={row.response_id}>
                    <td className="fixedWidth alignLeft wrapText">
                        {row.display_name}
                    </td>
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                StringUtil.numberToLocaleString(row.count)
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    <td className="alignCenter">
                        {isOpinion ? Strings['en'].common.NOT_APPLICABLE : (row.correct ? Strings['en'].common.YES : Strings['en'].common.NO)}
                    </td>
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                <PercentChosenElem
                                    total={grandTotalCount}
                                    count={row.count}
                                />
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                <AvgTimePlayedElem time_spent_total={row.time_spent || 0} total_answers={row.count} />
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    {categoryImpacts}
                </tr>
            )
        });
        
        return (
            <table className="qlaQuestionDetailTable">
                <thead>
                    <tr>
                        <th className="fixedWidth alignLeft">
                            {Strings['en'].activity.qla.questions.QS_THEAD_RESPONSE}
                        </th>
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCount"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT_TT}
                            />
                        }
                        <TH_QLA_Question className="alignLeft" question={question} partCode="qCorrect"
                            headingText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT}
                            tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT_TT}
                        />

                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qPercent"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT_TT}
                            />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qTime"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_TS}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_TS_TT}
                            />
                        }
                        {categoryNames}
                    </tr>
                </thead>
                <tbody>
                    {rowElems}
                </tbody>
            </table>
        );
    }

    renderFIBQDetails=(details,question)=>{
        let categoryNames = [];
        let headingMap = {};
        let grandTotalCount = 0; // for percent calculation
        var isOpinion = (question.quality === Globals.analytics.question_quality.OPINION); // opinion = store, fact = assess
        
        _.forEach(details,(row)=>{
            grandTotalCount += (Number(row.count) || 0);
            _.forEach(row.impacts,(impact)=>{
                if (typeof headingMap[impact.id] === 'undefined') {
                    headingMap[impact.id] = categoryNames.length;
                    categoryNames.push(
                        <TH_QLA_Impact key={impact.id} question={question} impact={impact}/>
                    );
                }
            });
        });
        let rowElems = [];
        _.forEach(details,(row)=>{
            let categoryImpacts = [];
            _.forEach(headingMap,(index,impact_id)=>{
                const imp = _.find(row.impacts,{id: impact_id});
                categoryImpacts[index] = (
                    <td className="alignCenter" key={index}>
                        { (imp && imp.impact) ? imp.impact : '' }
                    </td>
                );
            });
            rowElems.push(
                <tr key={row.response_id}>
                    <td className="fixedWidth alignLeft wrapText">
                        {isOpinion ? Strings['en'].common.NOT_APPLICABLE : row.display_name}
                    </td>
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                StringUtil.numberToLocaleString(row.count)
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    { isOpinion ? null : 
                    <td className="alignCenter">
                        {row.correct ? Strings['en'].common.YES : Strings['en'].common.NO}
                    </td>
                    }
                    {(isOpinion || this.props.hideDataColumns) ?
                        null
                        : 
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                <PercentChosenElem
                                    total={grandTotalCount}
                                    count={row.count}
                                />
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                <AvgTimePlayedElem time_spent_total={row.time_spent || 0} total_answers={row.count} />
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    {categoryImpacts}
                </tr>
            )
        });
        return (
            <table className="qlaQuestionDetailTable">
                <thead>
                    <tr>
                        <th className="fixedWidth alignLeft">
                            {Strings['en'].activity.qla.questions.QS_THEAD_RESPONSE}
                        </th>
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCount"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT_TT}
                            />
                        }
                        {(isOpinion) ? null : 
                        <TH_QLA_Question className="alignLeft" question={question} partCode="qCorrect"
                            headingText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT}
                            tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT_TT}
                        />
                        }
                        {(isOpinion || this.props.hideDataColumns) ? null : 
                        <TH_QLA_Question className="alignLeft" question={question} partCode="qPercent"
                            headingText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT}
                            tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT_TT}
                        />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qTime"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_TS}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_TS_TT}
                            />
                        }
                        {categoryNames}
                    </tr>
                </thead>
                <tbody>
                    {rowElems}
                </tbody>
            </table>
        );
    }

    renderATAQDetails=(details,question)=>{
        let categoryNames = [];
        let headingMap = {};
        var isOpinion = (question.quality === Globals.analytics.question_quality.OPINION);
        
        _.forEach(details,(row)=>{
            _.forEach(row.impacts,(impact)=>{
                if (typeof headingMap[impact.id] === 'undefined') {
                    headingMap[impact.id] = categoryNames.length;
                    categoryNames.push(
                        <TH_QLA_Impact key={impact.id} question={question} impact={impact}/>
                    );
                }
            });
        });
        let rowElems = [];
        _.forEach(details,(row)=>{
            let categoryImpacts = [];
            _.forEach(headingMap,(index,impact_id)=>{
                const imp = _.find(row.impacts,{id: impact_id});
                categoryImpacts[index] = (
                    <td className="alignCenter" key={index}>
                        { (imp && imp.impact) ? imp.impact : '' }
                    </td>
                );
            });
            rowElems.push(
                <tr key={row.response_id}>
                    <td className="fixedWidth alignLeft wrapText">
                        {row.display_name}
                    </td>
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                StringUtil.numberToLocaleString(row.count)
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    <td className="alignCenter">
                        {isOpinion ? Strings['en'].common.NOT_APPLICABLE : (row.correct ? Strings['en'].common.YES : Strings['en'].common.NO)}
                    </td>
                    {(!this.props.hideDataColumns) &&
                        <td className="alignCenter">
                            {(row.count !== 0 || !this.props.showCharacterWhenZero) ?
                                <PercentChosenElem
                                    total={question.total_attempts}
                                    count={row.count}
                                />
                                :
                                ZERO_CHAR
                            }
                        </td>
                    }
                    {categoryImpacts}
                </tr>
            )
        });
        return (
            <table className="qlaQuestionDetailTable">
                <thead>
                    <tr>
                        <th className="fixedWidth alignLeft">
                            {Strings['en'].activity.qla.questions.QS_THEAD_RESPONSE}
                        </th>
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCount"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT_ATAQ_TT}
                            />
                        }
                        <TH_QLA_Question className="alignLeft" question={question} partCode="qCorrect"
                            headingText={Strings['en'].activity.qla.questions.QS_THEAD_STUD_SHLD_SEL}
                            tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_STUD_SHLD_SEL_TT}
                        />
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qPercent"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT_TT}
                            />
                        }
                        {categoryNames}
                    </tr>
                </thead>
                <tbody>
                    {rowElems}
                </tbody>
            </table>
        );
    }

    renderCATQDetails=(details,question)=>{
        let categoryNames = [];
        let headingMap = {};
        _.forEach(details,(catRow)=>{
            _.forEach(catRow.items,(row)=>{
                _.forEach(row.impacts,(impact)=>{
                    if (typeof headingMap[impact.id] === 'undefined') {
                        headingMap[impact.id] = categoryNames.length;
                        categoryNames.push(
                            <TH_QLA_Impact key={impact.id} question={question} impact={impact}/>
                        );
                    }
                });
            });
        });
        let rowElems = [];
        
        // if we have any blank category names (and they aren't all blank), we'll give it a special name (distractor)
        var distractorName = '';
        var blankCats = _.filter(details,{category_name: ''});
        if (blankCats.length < details.length) {
            distractorName = Strings['en'].activity.qla.questions.QS_TD_DISTRACTORS;
        }
        
        _.forEach(details,(catRow)=>{
            rowElems.push(
                <tr key={catRow.cat_id}>
                    <td colSpan={3 + categoryNames.length} className="fixedWidth alignLeft wrapText">
                        {`${Strings['en'].activity.qla.questions.QA_TD_CATEGORIES}: ${(catRow.category_name || distractorName || catRow.cat_id)}`}
                    </td>
                </tr>
            );
            _.forEach(catRow.items,(row)=>{
                let categoryImpacts = [];
                _.forEach(headingMap,(index,impact_id)=>{
                    const imp = _.find(row.impacts,{id: impact_id});
                    categoryImpacts[index] = (
                        <td key={index} className="alignCenter">
                            { (imp && imp.impact) ? imp.impact : '' }
                        </td>
                    );
                });
                rowElems.push(
                    <tr key={row.item_id}>
                        <td className="fixedWidth alignLeftIndent wrapText">
                            {row.item_name}
                        </td>
                        {(!this.props.hideDataColumns) &&
                            <td className="alignCenter">
                                {(row.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                    StringUtil.numberToLocaleString(row.total_correct)
                                    :
                                    ZERO_CHAR
                                }
                            </td>
                        }
                        {(!this.props.hideDataColumns) &&
                            <td className="alignCenter">
                                {(row.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                    <PercentChosenElem total={row.total_correct} count={row.total_answers} />
                                    :
                                    ZERO_CHAR
                                }
                            </td>
                        }
                        {categoryImpacts}
                    </tr>
                )
            }); // for items
        }); // for categories
        return (
            <table className="qlaQuestionDetailTable">
                <thead>
                    <tr>
                        <th className="alignLeft">
                            {Strings['en'].activity.qla.questions.QS_THEAD_RESPONSE}
                        </th>
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCount"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT_TT}
                            />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qPercent"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT_CAT_TT}
                            />
                        }
                        {categoryNames}
                    </tr>
                </thead>
                <tbody>
                    {rowElems}
                </tbody>
            </table>
        );
    } // renderCATQDetails

    renderMatchDetails=(details, question) => {
        let rowElems = [];
        _.forEach(details, (catRow) => {
            rowElems.push(
                <tr key={catRow.cat_id}>
                    <td colSpan={4} className="fixedWidth alignLeft wrapText">
                        {`${Strings['en'].activity.qla.questions.QA_TD_CATEGORIES}: ${(catRow.category_name || catRow.cat_id)}`}
                    </td>
                </tr>
            );
            _.forEach(catRow.items, (row) => {
                rowElems.push(
                    <tr key={row.item_id}>
                        <td className="fixedWidth alignLeftIndent wrapText">
                            {row.item_name}
                        </td>
                        {(!this.props.hideDataColumns) &&
                            <td className="alignCenter">
                                {(row.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                    StringUtil.numberToLocaleString(row.total_answers)
                                    :
                                    ZERO_CHAR
                                }
                            </td>
                        }
                        {(question.quality !== Globals.analytics.question_quality.OPINION) &&
                            <td className="alignCenter">
                                {(typeof row.correct === 'boolean') ?
                                    (row.correct) ? Strings['en'].common.YES : Strings['en'].common.NO
                                    :
                                    null
                                }
                            </td>
                        }
                        {(!this.props.hideDataColumns) &&
                            <td className="alignCenter">
                                {(row.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                    <PercentChosenElem
                                        total={question.total_answers}
                                        count={row.total_answers}
                                    />
                                    :
                                    ZERO_CHAR
                                }
                            </td>
                        }
                    </tr>
                )
            }); // for items
        }); // for categories
        return (
            <table className="qlaQuestionDetailTable">
                <thead>
                    <tr>
                        <th className="fixedWidth alignLeft">
                            {Strings['en'].activity.qla.questions.QS_THEAD_RESPONSE}
                        </th>
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCount"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_COUNT_TT}
                            />
                        }
                        {(question.quality !== Globals.analytics.question_quality.OPINION) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qCorrect"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_CORRECT_TT}
                            />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA_Question className="alignLeft" question={question} partCode="qPercent"
                                headingText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT}
                                tooltipText={Strings['en'].activity.qla.questions.QS_THEAD_PERCENT_TT}
                            />
                        }
                    </tr>
                </thead>
                <tbody>
                    {rowElems}
                </tbody>
            </table>
        );
    } // renderCATQDetails
    
    renderDetails=(question)=>{
        const details = this.props.questionDetails[question.id].questionDetails;
        switch(question.question_type) {
            case Globals.analytics.question_type.MCQ:
                return this.renderMCQDetails(details,question);
            case Globals.analytics.question_type.FIBQ:
                return this.renderFIBQDetails(details,question);
            case Globals.analytics.question_type.ATAQ:
                return this.renderATAQDetails(details,question);
            case Globals.analytics.question_type.CATQ:
                return this.renderCATQDetails(details,question);
            case Globals.analytics.question_type.MATCH:
                return this.renderMatchDetails(details,question);
        }
    }
        
	render() {
        const orderedQuestionSummary = _.orderBy(
            this.props.questionSummary,
            [
                'question_order', // new pattern
                'question_type', // fallback to old pattern
                'id', // fallback to old pattern 2
            ]
        );

        let colspan = 5;
        if (!this.props.hideDataColumns) {
            colspan += 3;
        }
        
		return (
			<table className="qlaQuestionTable">
				<thead>
					<tr>
						<th>{/*Expansion Control*/}</th>
						<th className="alignLeft">
							{Strings['en'].activity.qla.questions.QA_THEAD_Q_PROMPT}
						</th>
                        <TH_QLA className="alignLeft" th_id="qlaQsTable-qType-th" tt_id="qlaQsTable-qType-tt"  
                            headingText={Strings['en'].activity.qla.questions.QA_THEAD_Q_TYPE} 
                            tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_Q_TYPE_TT}
                        />
                        <TH_QLA className="alignLeft" th_id="qlaQsTable-quality-th" tt_id="qlaQsTable-quality-tt"  
                            headingText={Strings['en'].activity.qla.questions.QA_THEAD_QUALITY} 
                            tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_QUALITY_TT}
                        />
                        <TH_QLA th_id="qlaQsTable-loop-th" tt_id="qlaQsTable-loop-tt"  
                            headingText={Strings['en'].activity.qla.questions.QA_THEAD_LOOP} 
                            tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_LOOP_TT}
                        />
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA th_id="qlaQsTable-totAns-th" tt_id="qlaQsTable-totAns-tt"  
                                headingText={Strings['en'].activity.qla.questions.QA_THEAD_TOTAL_ANS} 
                                tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_TOTAL_ANS_TT}
                            />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA th_id="qlaQsTable-pctCorrect-th" tt_id="qlaQsTable-pctCorrect-tt"  
                                headingText={Strings['en'].activity.qla.questions.QA_THEAD_PCT_CORRECT} 
                                tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_PCT_CORRECT_TT}
                            />
                        }
                        {(!this.props.hideDataColumns) &&
                            <TH_QLA th_id="qlaQsTable-ts-th" tt_id="qlaQsTable-ts-tt"  
                                headingText={Strings['en'].activity.qla.questions.QA_THEAD_TS} 
                                tooltipText={Strings['en'].activity.qla.questions.QA_THEAD_TS_TT}
                            />
                        }
					</tr>
				</thead>
				<tbody>
					{orderedQuestionSummary.map((question) => {
                        const qid = `question_display_name_${question.id}`;
                        const detailsAreExpanded = this.detailsAreExpanded(question);
                        const detailsArePending = this.detailsArePending(question);
                        const detailsAreLoaded = this.detailsAreLoaded(question);
                        const detailsRowId = `details-${question.id}`;
                        return (
                            <React.Fragment key={`frag-${question.id}`}>
                                <tr className={detailsAreExpanded ? "expanded" :""}>
                                    <td>
                                        <ExpansionButton
                                            className="exp-button__smImage"
                                            disabled={!this.canToggleDetails(question)}
                                            expanded={detailsAreExpanded}
                                            controlTitle={this.getTitleForToggleControl(question)}
                                            ariaText="Question Details"
                                            ariaDescribedBy={qid}
                                            ariaControls={detailsRowId}
                                            onClick={()=>{this.onToggleDetails(question)}}
                                            expandedImg={!detailsArePending ? `${GlobalConfig.StaticUrl}/img/caret-down-fill.svg` : ''}
                                            collapsedImg={!detailsArePending ? `${GlobalConfig.StaticUrl}/img/caret-right-fill.svg` : ''}
                                            expandedIcon={detailsArePending ? 'fa-refresh fa-spin' : ''}
                                            collapsedIcon={detailsArePending ? 'fa-refresh fa-spin' : ''}
                                            
                                        />
                                    </td>
                                    <td className="alignLeft wrapText" id={qid}>{question.display_name}</td>
                                    <td>{StringUtil.getStringAbbrevForQuestionType(question.question_type, question.quality)}</td>
                                    <td>{StringUtil.getStringForQuestionQuality(question.quality)}</td>
                                    <td>{StringUtil.formatBoolYesNo(question.multiple_attempts)}</td>
                                    {(!this.props.hideDataColumns) &&
                                        <td>
                                            {(question.total_attempts !== 0 || !this.props.showCharacterWhenZero) ?
                                                StringUtil.numberToLocaleString(question.total_attempts)
                                                :
                                                ZERO_CHAR
                                            }
                                        </td>
                                    }
                                    {(!this.props.hideDataColumns) &&
                                        <td>
                                            {(question.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                                <PercentCorrectElem total_correct={question.total_correct} total_answers={question.total_answers} quality={question.quality} />
                                                :
                                                ZERO_CHAR
                                            }
                                        </td>
                                    }
                                    {(!this.props.hideDataColumns) &&
                                        <td>
                                            {(question.total_answers !== 0 || !this.props.showCharacterWhenZero) ?
                                                <AvgTimePlayedElem time_spent_total={question.time_spent_total} total_answers={question.total_answers} />
                                                :
                                                ZERO_CHAR
                                            }
                                        </td>
                                    }
                                </tr>
                                <tr id={detailsRowId} className="detailsRow" aria-live="polite" aria-atomic="true">
                                    {(detailsAreExpanded) &&
                                        <td className={!detailsAreLoaded ? 'loading' : '' } colSpan={colspan}>
                                            {(detailsAreLoaded) ?
                                                this.renderDetails(question)
                                                :
                                                Strings['en'].common.LOADING
                                            }
                                        </td>
                                    }
                                </tr>
                            </React.Fragment>
                        );
					})}
				</tbody>
			</table>
		);
	}
}

QLAQuestionSummaryTable.defaultProps = defaultProps;
QLAQuestionSummaryTable.propTypes = propTypes;

function PercentCorrectElem(props) {
    if (props.quality === Globals.analytics.question_quality.OPINION) {
        return <span>{Strings['en'].common.NOT_APPLICABLE}</span>
    }

	const percentCorrect = props.total_answers > 0 ? parseInt(props.total_correct /  props.total_answers * 100, 10) : 0;
	let pcRangeClass = 'pcRange--noRes';
	if (props.total_answers > 0) {
		if (percentCorrect < 0 || percentCorrect > 100) {
			console.warn('PercentCorrectElem encountered invalid percent', percentCorrect);
			pcRangeClass = 'pcRange--noRes';
		}
		else if (percentCorrect >= 90 && percentCorrect <= 100) {
			pcRangeClass = 'pcRange--high';
		}
		else if (percentCorrect >= 60 && percentCorrect < 90) {
			pcRangeClass = 'pcRange--midHigh';
		}
		else if (percentCorrect >= 20 && percentCorrect < 60) {
			pcRangeClass = 'pcRange--midLow';
		}
		else if (percentCorrect >= 0 && percentCorrect < 20) {
			pcRangeClass = 'pcRange--low';
		}
	}

	return (
		<span className={pcRangeClass}>
			{percentCorrect}%
		</span>
	);
}

PercentCorrectElem.propTypes = {
    quality: PropTypes.oneOf(Object.values(Globals.analytics.question_quality)).isRequired,
	total_correct: PropTypes.number.isRequired,
	total_answers: PropTypes.number.isRequired,
};


function PercentChosenElem(props) {
    let bubbleRangeClass = 'pcRange--noRes';
    let percentString = '';
    if (props.total > 0) {
        percentString = `${Math.floor(props.count * 100 / props.total)}%`;
        /*if (
            props.quality !== Globals.analytics.question_quality.OPINION
            && typeof props.correct === 'boolean' // null may be used if we don't know whether something is correct or not (match category/choice with no responses)
        ) {
            if (props.correct) {
                bubbleRangeClass = 'pcRange--high';
            }
            else {
                bubbleRangeClass = 'pcRange--low';
            }

        }*/

    }

    return (
        <span className={bubbleRangeClass}>
            {percentString}
        </span>
    );
}

PercentChosenElem.propTypes = {
    // correct: PropTypes.oneOfType([
    //     PropTypes.bool,
    //     PropTypes.object // account for allowing null
    // ]),
    // quality: PropTypes.oneOf(Object.values(Globals.analytics.question_quality)).isRequired,
    count: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
};

PercentChosenElem.defaultProps = {};

function AvgTimePlayedElem(props) {
	const avgTp = props.total_answers > 0 ? props.time_spent_total /  props.total_answers : 0;
    /* color no longer used due to Redmine #5102 (not making value judgement regarding time played)
	let tpRangeClass = 'tpRange--noRes';
	if (props.total_answers > 0) { // TODO using arbitrary values until ranges are defined in design
		if (avgTp > 10000) {
			tpRangeClass = 'tpRange--high';
		}
		else if (avgTp >= 10000 && avgTp <= 10000) {
			tpRangeClass = 'tpRange--midHigh';
		}
		else if (avgTp >= 5000 && avgTp < 10000) {
			tpRangeClass = 'tpRange--midLow';
		}
		else if (avgTp >= 0 && avgTp < 5000) {
			tpRangeClass = 'tpRange--low';
		}
	}*/

	return StringUtil.getTimeStringForMs(avgTp);
}

AvgTimePlayedElem.propTypes = {
	time_spent_total: PropTypes.number.isRequired,
	total_answers: PropTypes.number.isRequired,
};

AvgTimePlayedElem.defaultProps = {};